import { useState, useCallback } from 'react';

export type ModuleColor = 'blue' | 'green' | 'yellow' | 'purple';

export interface CommonUsageItem {
  module: string;
  moduleColor?: ModuleColor;
  api: string;
  role: string;
  snippet: string;
}

export interface CommonUsagePanelProps {
  items: CommonUsageItem[];
  defaultOpen?: boolean;
  title?: string;
  /** R8-A-010: i18n-translated subtitle shown in the panel header description area.
   *  If omitted, defaults to the hardcoded Korean/English mixed fallback string. */
  subtitle?: string;
}

function modulePillStyle(color?: ModuleColor): React.CSSProperties {
  const map: Record<ModuleColor, { color: string; border: string }> = {
    blue:   { color: 'var(--color-info)',    border: 'var(--color-info)' },
    green:  { color: 'var(--color-success)', border: 'var(--color-success)' },
    yellow: { color: 'var(--color-warning)', border: 'var(--color-warning)' },
    purple: { color: 'var(--color-brand-primary)', border: 'var(--color-brand-primary)' },
  };
  const resolved = map[color ?? 'blue'];
  return {
    display: 'inline-block',
    fontSize: 'var(--font-size-xs)',
    fontWeight: 600,
    padding: '1px var(--spacing-2)',
    borderRadius: 'var(--radius-full)',
    border: `1px solid ${resolved.border}`,
    color: resolved.color,
    whiteSpace: 'nowrap' as const,
  };
}

const UNIQUE_MODULES = [
  { label: 'eraf-core',      color: 'blue'   as ModuleColor },
  { label: 'eraf-data-jpa',  color: 'green'  as ModuleColor },
  { label: 'eraf-security',  color: 'yellow' as ModuleColor },
];

export function CommonUsagePanel({
  items,
  defaultOpen = false,
  title = '이 페이지가 사용한 공통 모듈',
  subtitle = '§6.7 Common Usage Showcase — ERAF 공통 자산 활용 사례',
}: CommonUsagePanelProps) {
  const [open, setOpen] = useState(defaultOpen);

  const toggle = useCallback(() => setOpen((v) => !v), []);
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        toggle();
      }
    },
    [toggle]
  );

  return (
    <div
      style={{
        marginTop: 'var(--spacing-6)',
        border: '1px solid var(--color-surface-border)',
        borderRadius: 'var(--radius-md)',
        overflow: 'hidden',
      }}
      aria-label="이 페이지가 사용한 공통 모듈"
    >
      {/* Summary row */}
      <div
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={toggle}
        onKeyDown={handleKeyDown}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--spacing-2)',
          padding: 'var(--spacing-3) var(--spacing-4)',
          background: 'var(--color-surface-muted)',
          borderBottom: open ? '1px solid var(--color-surface-border)' : undefined,
          cursor: 'pointer',
          fontSize: 'var(--font-size-sm)',
          fontWeight: 600,
          color: 'var(--color-text-secondary)',
          userSelect: 'none',
        }}
      >
        {/* Chevron */}
        <span
          style={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-brand-primary)',
            transition: 'transform 0.2s',
            display: 'inline-block',
            transform: open ? 'none' : 'rotate(-90deg)',
          }}
          aria-hidden="true"
        >
          ▼
        </span>

        {title}

        {/* Count badge */}
        <span
          style={{
            background: 'var(--color-brand-primary)',
            color: 'var(--color-text-inverse)',
            fontSize: 'var(--font-size-xs)',
            fontWeight: 700,
            padding: '1px var(--spacing-2)',
            borderRadius: 'var(--radius-full)',
          }}
        >
          {items.length}
        </span>

        {/* Module badges */}
        <span style={{ display: 'flex', gap: 'var(--spacing-1)', flexWrap: 'wrap' as const }}>
          {UNIQUE_MODULES.map((m) => (
            <span key={m.label} style={modulePillStyle(m.color)}>
              {m.label}
            </span>
          ))}
        </span>

        {/* Description — R8-A-010: rendered from prop (i18n key passed by caller) */}
        <span
          style={{
            marginLeft: 'auto',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-muted)',
            fontWeight: 400,
          }}
        >
          {subtitle}
        </span>
      </div>

      {/* Table */}
      {open && (
        <div style={{ overflowX: 'auto', padding: 'var(--spacing-4)' }}>
          <table
            style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}
            aria-label="공통 모듈 사용 내역"
          >
            <thead>
              <tr>
                {(['모듈', 'API (공통 능력 코드)', '이 페이지에서의 역할', '사용 예 (1줄)'] as const).map((col) => (
                  <th
                    key={col}
                    style={{
                      textAlign: 'left',
                      padding: 'var(--spacing-2) var(--spacing-3)',
                      background: 'var(--color-surface-elevated)',
                      color: 'var(--color-text-secondary)',
                      fontWeight: 600,
                      borderBottom: '1px solid var(--color-surface-border)',
                      whiteSpace: 'nowrap' as const,
                      fontSize: 'var(--font-size-xs)',
                      textTransform: 'uppercase' as const,
                      letterSpacing: '0.05em',
                    }}
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => (
                <tr
                  key={idx}
                  style={{
                    borderBottom:
                      idx < items.length - 1
                        ? '1px solid var(--color-surface-border)'
                        : undefined,
                  }}
                >
                  {/* Module */}
                  <td
                    style={{
                      padding: 'var(--spacing-2) var(--spacing-3)',
                      verticalAlign: 'top',
                      whiteSpace: 'nowrap' as const,
                    }}
                  >
                    <span style={modulePillStyle(item.moduleColor)}>{item.module}</span>
                  </td>
                  {/* API */}
                  <td
                    style={{
                      padding: 'var(--spacing-2) var(--spacing-3)',
                      verticalAlign: 'top',
                      fontFamily: 'var(--font-family-mono)',
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--color-brand-primary)',
                    }}
                  >
                    {item.api}
                  </td>
                  {/* Role */}
                  <td
                    style={{
                      padding: 'var(--spacing-2) var(--spacing-3)',
                      verticalAlign: 'top',
                      color: 'var(--color-text-secondary)',
                      minWidth: '160px',
                    }}
                  >
                    {item.role}
                  </td>
                  {/* Snippet */}
                  <td
                    style={{
                      padding: 'var(--spacing-2) var(--spacing-3)',
                      verticalAlign: 'top',
                    }}
                  >
                    <code
                      style={{
                        fontFamily: 'var(--font-family-mono)',
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--color-success)',
                        background: 'var(--color-surface-elevated)',
                        padding: '2px var(--spacing-2)',
                        borderRadius: 'var(--radius-sm)',
                        whiteSpace: 'nowrap' as const,
                        display: 'inline-block',
                      }}
                    >
                      {item.snippet}
                    </code>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
